import { playlistsContaining, type Playlist } from '@visious/core';
import { fmt, text } from '../strings';
import { membershipNote } from './model';

/**
 * The add-to-playlist rows, as reasoning. Its own file rather than part of `model.ts` because
 * `AddToPlaylist.tsx` is a `.tsx` and must not import `@visious/core` (R13) — the same split
 * `listing/model.ts` has.
 */

export interface AddTargetRow {
  readonly playlistId: string;
  readonly title: string;
  readonly countText: string | null;
  readonly note: string | null;
  /** Disabled only for a playlist we *know* holds it, or one with a write in flight. */
  readonly disabled: boolean;
  readonly state: 'idle' | 'adding' | 'added' | 'failed';
}

export function addTargetRows(
  playlists: readonly Playlist[] | null,
  videoId: string,
  outcomes: ReadonlyMap<string, 'added' | 'failed'>,
  busy: ReadonlySet<string>,
): readonly AddTargetRow[] | null {
  if (playlists === null) return null;
  const membership = playlistsContaining(playlists, videoId);

  return playlists.map((playlist): AddTargetRow => {
    const outcome = outcomes.get(playlist.playlistId);
    const adding = busy.has(playlist.playlistId);
    const present = membership.get(playlist.playlistId) ?? 'unknown';
    const title = playlist.title.trim();

    return {
      playlistId: playlist.playlistId,
      title: title === '' ? text().playlists.untitled : playlist.title,
      // `videoCount` rather than `entries.length`: the array is capped at 100 by the endpoint, so a
      // 340-entry playlist would otherwise report 100 (`toOwnedPlaylistRow` says the same).
      countText: fmt().countedNoun(playlist.videoCount, text().playlists.video),
      note: outcome === 'added' ? null : membershipNote(present),
      /*
       * Disabled for "already in it" and never for "cannot tell" — the two must not collapse. A
       * playlist longer than the embedded window may hold the video at position 250, and refusing the
       * add on a guess would make a legitimate action impossible; allowing it risks a duplicate, which
       * the reader can see and remove. One of those is recoverable.
       *
       * Also disabled once *this session* added it, which is knowledge the payload did not have to give.
       */
      disabled: adding || present === 'present' || outcome === 'added',
      state: adding ? 'adding' : outcome === 'added' ? 'added' : outcome === 'failed' ? 'failed' : 'idle',
    };
  });
}
